import type { MtoAnomaly, NsiMapping } from '@entities/mto';
import { anomalyKindLabel } from '@entities/mto';
import type { AnomalyGroupData, BulkAction } from './types';

/** До стольких замечаний — простой список; больше — группы по видам. */
export const ANOMALIES_FLAT_LIMIT = 8;
/** Группа от стольких замечаний свёрнута: «не заказано» в сверке бывает по
 *  сотне строк и повторяет таблицу выше. Критичные не сворачиваем. */
export const GROUP_COLLAPSE_AT = 6;
export const GROUP_PAGE = 50;

/** Строк сопоставления на странице: у ведомости объекта их сотни. */
export const MAPPINGS_PAGE = 100;

export const SEVERITY_RANK: Readonly<Record<string, number>> = { critical: 2, major: 1 };

/** Наименование привязанной позиции НСИ: ищем кандидата по `nomenclature_id`,
 *  иначе показываем сам идентификатор (на случай, если кандидата нет в списке). */
export function mappedNomenclatureName(mapping: NsiMapping): string | null {
  if (!mapping.nomenclature_id) return null;
  const match = mapping.candidates.find((c) => c.nomenclature_id === mapping.nomenclature_id);
  return match ? match.name : mapping.nomenclature_id;
}

/** Сводка сопоставлений для заголовка свёрнутого блока. */
export function mappingCounts(mappings: readonly NsiMapping[]) {
  return {
    linked: mappings.filter(
      (m) => m.nomenclature_id && (m.status === 'auto' || m.status === 'verified'),
    ).length,
    pending: mappings.filter((m) => m.status === 'pending' && m.match_type !== 'none').length,
    none: mappings.filter((m) => m.match_type === 'none').length,
  };
}

/** Количество с единицей в одну ячейку: `120 т`, либо прочерк. Кол-во — строка
 *  (Decimal), не number; не приводим. */
export function qtyWithUnit(quantity: string | null, unit: string | null): string {
  if (!quantity) return '—';
  return unit ? `${quantity} ${unit}` : quantity;
}

/** Источник позиции поставки: ссылка + имя файла, если ссылка его ещё не
 *  называет (обычно она уже «файл X.xlsx, стр. 2» — имя дважды не пишем). */
export function supplySource(sourceRef: string, sourceFile: string | null): string {
  if (sourceFile && !sourceRef.includes(sourceFile)) return `${sourceFile} · ${sourceRef}`;
  return sourceRef;
}

/** Срок/буфер: число — `45 дн.`, текст из реестра («3 месяца», «8 недель») —
 *  как есть, пусто — прочерк. Не приводим: срок переносится из документа. */
export function daysOrDash(value: string | null): string {
  if (!value || !value.trim()) return '—';
  return /^\d+(?:[.,]\d+)?$/.test(value.trim()) ? `${value.trim()} дн.` : value;
}

/** Цена с валютой в одну ячейку: `1 200 ₽`, либо прочерк. Цена — строка (перенос
 *  как есть), не number; не приводим — добавляем только подпись валюты. */
export function priceWithCurrency(price: string | null, currency: string | null): string {
  if (!price || !price.trim()) return '—';
  return currency ? `${price} ${currency}` : price;
}

/** Остаток отрицательный? Кол-во — строка (перенос как есть); парсим мягко
 *  (запятая → точка, отбрасываем хвост) только для подсветки строки-ошибки. */
export function isNegativeStock(quantity: string | null): boolean {
  if (!quantity) return false;
  const n = Number.parseFloat(quantity.replace(/\s| /g, '').replace(',', '.'));
  return Number.isFinite(n) && n < 0;
}

export function bulkQuestion(a: BulkAction): string {
  const what = a.kind
    ? `замечания «${anomalyKindLabel(a.kind) || a.kind}»`
    : 'все неразобранные замечания';
  return a.decision === 'acknowledged'
    ? `Учесть ${what} (${a.count})? Они войдут в служебную записку.`
    : `Отклонить ${what} (${a.count})? В служебную записку они не войдут.`;
}

/** Неразобранные и важные — выше. */
export function anomalyRank(a: MtoAnomaly): number {
  return (a.decision ? 0 : 4) + (SEVERITY_RANK[a.severity] ?? 0);
}

export function groupAnomalies(anomalies: readonly MtoAnomaly[]): AnomalyGroupData[] {
  const by = new Map<string, MtoAnomaly[]>();
  for (const a of anomalies) {
    const list = by.get(a.kind);
    if (list) list.push(a);
    else by.set(a.kind, [a]);
  }
  const groups = [...by.entries()].map(([kind, items]) => ({
    kind,
    items: [...items].sort((a, b) => anomalyRank(b) - anomalyRank(a)),
    undecided: items.filter((a) => !a.decision).length,
    worst: items.reduce(
      (w, a) => ((SEVERITY_RANK[a.severity] ?? 0) > (SEVERITY_RANK[w] ?? 0) ? a.severity : w),
      items[0]?.severity ?? 'minor',
    ),
  }));
  const weight = (g: AnomalyGroupData) =>
    (g.undecided > 0 ? 10 : 0) + (SEVERITY_RANK[g.worst] ?? 0);
  return groups.sort(
    (a, b) => weight(b) - weight(a) || b.undecided - a.undecided || b.items.length - a.items.length,
  );
}
